// Task Management enums, workflow rules and UI helpers.
// Mirrors the backend Task Management API (see docs/task-api-reference).
import { PERMISSIONS } from './permissions';

// ---------------- Enums ----------------
export const TASK_STATUSES = [
  'PENDING', 'ASSIGNED', 'ACCEPTED', 'IN_PROGRESS', 'WAITING_APPROVAL',
  'ON_HOLD', 'COMPLETED', 'CLOSED', 'CANCELLED', 'REJECTED', 'REOPENED',
];

export const TASK_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

export const TASK_DEPARTMENTS = [
  'SECURITY', 'MAINTENANCE', 'HOUSEKEEPING', 'ADMINISTRATION',
  'ACCOUNTS', 'MANAGEMENT', 'OTHER',
];

export const TASK_SOURCES = ['MANUAL', 'TEMPLATE', 'RECURRING', 'COMPLAINT', 'BULK', 'CLONE'];

export const ASSIGNMENT_ROLES = ['ASSIGNEE', 'DELEGATE'];

export const DEPENDENCY_TYPES = ['BLOCKED_BY', 'BLOCKS', 'RELATES_TO', 'DUPLICATES'];

export const PROJECT_STATUSES = ['PLANNING', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'ARCHIVED'];

export const PROJECT_USER_ROLES = ['OWNER', 'MANAGER', 'LEAD', 'SUPERVISOR'];

// ---------------- Workflow transitions ----------------
// Server enforces; client pre-validates to hide illegal actions.
export const STATUS_TRANSITIONS = {
  PENDING: ['ASSIGNED', 'IN_PROGRESS', 'ON_HOLD', 'CANCELLED'],
  ASSIGNED: ['ACCEPTED', 'REJECTED', 'IN_PROGRESS', 'ON_HOLD', 'CANCELLED'],
  ACCEPTED: ['IN_PROGRESS', 'ON_HOLD', 'CANCELLED'],
  IN_PROGRESS: ['WAITING_APPROVAL', 'COMPLETED', 'ON_HOLD', 'CANCELLED'],
  WAITING_APPROVAL: ['COMPLETED', 'IN_PROGRESS', 'REJECTED'],
  ON_HOLD: ['IN_PROGRESS', 'ASSIGNED', 'ACCEPTED', 'CANCELLED'],
  COMPLETED: ['CLOSED', 'REOPENED'],
  CLOSED: ['REOPENED'],
  REJECTED: ['ASSIGNED', 'IN_PROGRESS', 'CANCELLED', 'REOPENED'],
  REOPENED: ['IN_PROGRESS', 'ASSIGNED', 'ON_HOLD', 'CANCELLED'],
  CANCELLED: ['REOPENED'],
};

export const getAllowedTransitions = (status) => STATUS_TRANSITIONS[status] || [];

// The transition permission is dynamic — it depends on the target status.
export const getTransitionPermission = (targetStatus) => {
  if (targetStatus === 'COMPLETED' || targetStatus === 'REJECTED') return PERMISSIONS.TASK_APPROVE;
  if (targetStatus === 'REOPENED') return PERMISSIONS.TASK_REOPEN;
  if (targetStatus === 'CLOSED') return PERMISSIONS.TASK_CLOSE;
  return PERMISSIONS.TASK_TRANSITION;
};

// ---------------- UI badge helpers (Tailwind tw- classes) ----------------
export const STATUS_BADGE = {
  PENDING: 'tw-bg-gray-100 tw-text-gray-700',
  ASSIGNED: 'tw-bg-blue-100 tw-text-blue-700',
  ACCEPTED: 'tw-bg-indigo-100 tw-text-indigo-700',
  IN_PROGRESS: 'tw-bg-sky-100 tw-text-sky-700',
  WAITING_APPROVAL: 'tw-bg-amber-100 tw-text-amber-700',
  ON_HOLD: 'tw-bg-orange-100 tw-text-orange-700',
  COMPLETED: 'tw-bg-emerald-100 tw-text-emerald-700',
  CLOSED: 'tw-bg-teal-100 tw-text-teal-700',
  CANCELLED: 'tw-bg-gray-200 tw-text-gray-600',
  REJECTED: 'tw-bg-red-100 tw-text-red-700',
  REOPENED: 'tw-bg-purple-100 tw-text-purple-700',
};

export const PRIORITY_BADGE = {
  LOW: 'tw-bg-gray-100 tw-text-gray-600',
  MEDIUM: 'tw-bg-blue-100 tw-text-blue-700',
  HIGH: 'tw-bg-amber-100 tw-text-amber-700',
  CRITICAL: 'tw-bg-red-100 tw-text-red-700',
};

export const PROJECT_STATUS_BADGE = {
  PLANNING: 'tw-bg-blue-100 tw-text-blue-700',
  ACTIVE: 'tw-bg-emerald-100 tw-text-emerald-700',
  ON_HOLD: 'tw-bg-amber-100 tw-text-amber-700',
  COMPLETED: 'tw-bg-teal-100 tw-text-teal-700',
  ARCHIVED: 'tw-bg-gray-200 tw-text-gray-600',
};

// Solid fills for dashboard bars / segments — same hue family as the badges above,
// one step darker so a thin bar still reads against a white card.
export const STATUS_BAR = {
  PENDING: 'tw-bg-gray-400',
  ASSIGNED: 'tw-bg-blue-500',
  ACCEPTED: 'tw-bg-indigo-500',
  IN_PROGRESS: 'tw-bg-sky-500',
  WAITING_APPROVAL: 'tw-bg-amber-500',
  ON_HOLD: 'tw-bg-orange-500',
  COMPLETED: 'tw-bg-emerald-500',
  CLOSED: 'tw-bg-teal-500',
  CANCELLED: 'tw-bg-gray-300',
  REJECTED: 'tw-bg-red-500',
  REOPENED: 'tw-bg-purple-500',
};

export const PRIORITY_BAR = {
  LOW: 'tw-bg-gray-400',
  MEDIUM: 'tw-bg-blue-500',
  HIGH: 'tw-bg-amber-500',
  CRITICAL: 'tw-bg-red-500',
};

export const statusBarClass = (status) => STATUS_BAR[status] || 'tw-bg-gray-400';
export const priorityBarClass = (priority) => PRIORITY_BAR[priority] || 'tw-bg-gray-400';

export const statusBadgeClass = (status) => STATUS_BADGE[status] || 'tw-bg-gray-100 tw-text-gray-600';
export const priorityBadgeClass = (priority) => PRIORITY_BADGE[priority] || 'tw-bg-gray-100 tw-text-gray-600';
export const projectStatusBadgeClass = (status) => PROJECT_STATUS_BADGE[status] || 'tw-bg-gray-100 tw-text-gray-600';

// Turn PENDING / IN_PROGRESS into "Pending" / "In Progress" for display.
export const humanize = (value) =>
  typeof value === 'string'
    ? value.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')
    : value;
